import { useEffect, useMemo } from 'react';

import { toast } from '@raystack/apsara';
import { AxiosError } from 'axios';
import { debounce, isEqual } from 'lodash';
import moment from 'moment';
import useSWR, { mutate } from 'swr';

import { apiClient } from '@src/clients';
import { AssetReadModel } from '@src/clients/atlas/api';
import {
  ALLOWED_ASSET_TYPES,
  ASSET_STATUS_STRINGS,
  ASSET_TYPE_STRINGS,
  ATLAS_DATA_CUBE
} from '@src/utils/constants/ATLAS_CONSTANTS';

const SORT_ORDERS = {
  ASCEND: 'ASCEND',
  DESCEND: 'DESCEND'
} as const;

type SortKeys = keyof typeof SORT_ORDERS;

const sortAssets = (assets: AssetReadModel[], order: SortKeys) => {
  return assets.sort((a, b) => {
    const refDate = a.date.replaceAll('_', '-');
    const compDate = b.date.replaceAll('_', '-');

    if (order === SORT_ORDERS.DESCEND) {
      return moment(compDate).isBefore(refDate) ? -1 : 1;
    }

    return moment(compDate).isAfter(refDate) ? -1 : 1;
  });
};

const assetsErrorHelper = (error: AxiosError) => {
  if (error?.response?.status == 404) {
    console.error(error);
  } else {
    console.error('could not fetch aois', error);
    toast.error('Oops! Something went wrong.', {
      description:
        "We couldn't load the assets you requested. Please try again later"
    });
  }
};

const getCreatedAssets = (data: AssetReadModel[]) =>
  sortAssets(
    data?.filter(i => ALLOWED_ASSET_TYPES.includes(i.asset_type)) || [],
    SORT_ORDERS.DESCEND
  );

const getStacAssets = (data: AssetReadModel[]) =>
  sortAssets(
    data?.filter(
      asset =>
        asset.description !== ATLAS_DATA_CUBE &&
        asset.asset_type === ASSET_TYPE_STRINGS.stac_item
    ) || [],
    SORT_ORDERS.ASCEND
  );

const getCogAssets = (data: AssetReadModel[]) =>
  sortAssets(
    data?.filter(
      asset =>
        asset.description !== ATLAS_DATA_CUBE &&
        asset.asset_type === ASSET_TYPE_STRINGS.mosaic
    ) || [],
    SORT_ORDERS.ASCEND
  );

const inProgress = new Set<string>();
const refetch = (CACHE_KEY: string) => mutate(CACHE_KEY);
const debouncedRefetch = debounce(refetch, 2000);
let staticInstance = 0;

interface UseSceneAssets {
  aoiId?: string;
  cacheKey?: string;
}

export const useSceneAssets = ({
  aoiId,
  cacheKey = 'aoi_assets'
}: UseSceneAssets = {}) => {
  const CACHE_KEY = `${aoiId}/${cacheKey}`;

  const { isLoading, data } = useSWR(
    aoiId ? CACHE_KEY : null,
    () => apiClient.atlasApi.aoiId.listAssetsAoiIdAssetsGet(aoiId!),
    {
      onError: assetsErrorHelper,
      onErrorRetry: error => {
        // Never retry on 404.
        if (error.status === 404) return;
      },
      onSuccess: data => {
        const scenes = data.data;

        for (const scene of scenes) {
          if (scene.progress !== 100) {
            inProgress.add(scene.id);
            continue;
          }

          inProgress.delete(scene.id);
        }

        if (inProgress.size > 0) debouncedRefetch(CACHE_KEY);
      },
      revalidateOnFocus: false,
      refreshInterval: 0,
      compare: (a, b) => isEqual(a?.data, b?.data)
    }
  );

  const allAssetsLoaded = useMemo(() => {
    const filteredAssets =
      (data?.data as AssetReadModel[])?.filter(
        i =>
          i.description !== ATLAS_DATA_CUBE &&
          ALLOWED_ASSET_TYPES.includes(i.asset_type)
      ) ?? [];
    return !filteredAssets.some(i =>
      ASSET_STATUS_STRINGS.started.includes(i.status)
    );
  }, [data]);

  useEffect(() => {
    staticInstance++;

    return () => {
      staticInstance--;
      if (staticInstance === 0) debouncedRefetch.cancel();
    };
  }, []);

  return useMemo(() => {
    const scenes = data?.data ?? [];

    return {
      isLoading,
      allAssetsLoaded,
      allAssets: scenes,
      createdAssets: getCreatedAssets(scenes),
      stacAssets: getStacAssets(scenes),
      cogAssets: getCogAssets(scenes),
      mutate: () => {
        debouncedRefetch.cancel();
        refetch(CACHE_KEY);
      }
    };
  }, [data, isLoading, allAssetsLoaded]);
};
